import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";
const fmtAUD = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));

export default function ScriptsToProcessTab({ onProcessingComplete }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    phappsRequest(API_PATHS.ZEPHYR_PROCESSING_BATCH_SUMMARY, "GET")
      .then(res => setBatches(res?.data ?? []))
      .catch(() => setError("Failed to load unprocessed batches."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleProcessAll = async () => {
    setProcessing(true);
    setError(null);
    setSummary(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_PROCESSING_PROCESS_ALL, "POST");
      const data = res?.data ?? {};
      setSummary(data);
      setBatches([]);
      if (data.pricingReview > 0) onProcessingComplete?.(true);
    } catch {
      setError("Processing failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const totalScripts = batches.reduce((s, b) => s + Number(b.ScriptCount || 0), 0);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography fontWeight={700} fontSize="1rem" color={Z_PURPLE}>Unprocessed Script Batches</Typography>
          {!loading && !summary && (
            <Typography variant="body2" color="text.secondary">
              {batches.length} batch{batches.length !== 1 ? "es" : ""} · {totalScripts} scripts
            </Typography>
          )}
        </Box>
        {!summary && (
          <Button
            variant="contained"
            startIcon={processing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PlayArrowOutlinedIcon />}
            disabled={processing || batches.length === 0}
            onClick={handleProcessAll}
            sx={{ background: Z_PURPLE, "&:hover": { background: "#2a1240" }, fontWeight: 700, borderRadius: 2 }}
          >
            {processing ? "Processing…" : "Process All"}
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {summary && (
        <Alert severity="success" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
          <Typography fontWeight={700} mb={0.5}>Processing complete</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5 }}>
            {[
              ["Scripts processed", summary.total],
              ["Matched to existing items", summary.matched],
              ["Items auto-created", summary.autoCreated],
              ["Awaiting pricing review", summary.pricingReview],
              ["ComCare / VIC", summary.comcare],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography fontWeight={700}>{val}</Typography>
              </Box>
            ))}
          </Box>
          <Button size="small" sx={{ mt: 1, color: Z_BLUE }} onClick={() => { setSummary(null); load(); }}>
            Refresh list
          </Button>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress sx={{ color: Z_PURPLE }} />
        </Box>
      ) : !summary && batches.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
          <Typography>No unprocessed scripts.</Typography>
        </Box>
      ) : !summary && (
        <TableContainer component={Paper} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "none" }}>
          <Table size="small">
            <TableHead sx={{ background: `${Z_PURPLE}08` }}>
              <TableRow>
                {["Batch Code", "Chemist", "Batch Date", "Scripts", "Pharm Total"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1.2 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map(b => (
                <TableRow key={b.ScrBatchID} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.82rem" }}>{b.ScrBatchCode}</TableCell>
                  <TableCell>{b.ChemistName || "—"}</TableCell>
                  <TableCell>{fmtDate(b.ScrBatchDate)}</TableCell>
                  <TableCell>{b.ScriptCount}</TableCell>
                  <TableCell>{fmtAUD(b.TotalPharmAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
